import { useState, useRef, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import BackToDashboard from "@/components/BackToDashboard";
import api from "@/lib/api";
import { findAddressViolation } from "@/lib/addressGuard";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Sparkles,
  Check,
  QrCode,
  Landmark,
  IndianRupee,
  KeyRound,
  ImagePlus,
  X,
  AtSign,
  ChevronDown,
  ShieldCheck,
  Timer,
  Lock,
  HelpCircle,
  Trash2,
  MessageCircle,
  Instagram,
  Facebook,
  Youtube,
  Link2,
  Building2,
} from "lucide-react";

/**
 * ProfileInfo.jsx
 * ------------------------------------------------------------------
 * Merchant profile form — available on every plan.
 * - Loads real data from GET /profile on mount, saves via POST /profile.
 * - QR image uploads immediately on pick (POST /profile/qr-upload) —
 *   independent of the main "Save profile" button since it persists
 *   straight to businesses.qr_code_image_url on its own.
 * - Mandatory: name, email, phone (+ country code), country.
 * - Optional: address, bio.
 * - Payment method: Razorpay / UPI / QR / Bank — each with its own fields.
 * ------------------------------------------------------------------
 */

const COUNTRIES = [
  { code: "IN", name: "India", dial: "+91", currency: "INR" },
  { code: "US", name: "United States", dial: "+1", currency: "USD" },
  { code: "GB", name: "United Kingdom", dial: "+44", currency: "USD" },
  { code: "AE", name: "United Arab Emirates", dial: "+971", currency: "USD" },
  { code: "SG", name: "Singapore", dial: "+65", currency: "USD" },
  { code: "AU", name: "Australia", dial: "+61", currency: "USD" },
  { code: "CA", name: "Canada", dial: "+1", currency: "USD" },
  { code: "DE", name: "Germany", dial: "+49", currency: "USD" },
];

const PAYMENT_METHODS = [
  {
    key: "razorpay", label: "Razorpay", desc: "Cards, netbanking & wallets", icon: Sparkles, category: "automatic",
    fields: [
      { key: "razorpayId", label: "Razorpay Key ID", type: "text", icon: KeyRound, placeholder: "rzp_live_XXXXXXXXXXXX", required: true },
      { key: "razorpaySecret", label: "Razorpay Key Secret", type: "password", icon: ShieldCheck, secretFlagKey: "hasRazorpaySecret", required: true },
      {
        key: "razorpayWebhookSecret", label: "Razorpay Webhook Secret", type: "password", icon: ShieldCheck,
        secretFlagKey: "hasRazorpayWebhookSecret", required: true,
        hint: (
          <>
            From Razorpay Dashboard → Settings → Webhooks ·{" "}
            <Link to="/razorpay-keys-guide" target="_blank" className="font-semibold text-hotpink hover:underline">
              Where do I find my keys? →
            </Link>
          </>
        ),
      },
    ],
  },
  {
    key: "stripe", label: "Stripe", desc: "Global cards & wallets", icon: Globe, category: "automatic",
    fields: [
      { key: "stripeSecretKey", label: "Stripe Secret Key", type: "password", icon: KeyRound, placeholder: "sk_live_XXXXXXXXXXXX", secretFlagKey: "hasStripeSecret", required: true },
      {
        key: "stripeWebhookSecret", label: "Stripe Webhook Signing Secret", type: "password", icon: ShieldCheck,
        placeholder: "whsec_XXXXXXXXXXXX", secretFlagKey: "hasStripeWebhookSecret", required: true,
        hint: (
          <>
            From Stripe Dashboard → Developers → Webhooks ·{" "}
            <Link to="/stripe-keys-guide" target="_blank" className="font-semibold text-hotpink hover:underline">
              Where do I find my keys? →
            </Link>
          </>
        ),
      },
    ],
  },
  {
    key: "paypal", label: "PayPal", desc: "PayPal balance, cards & Pay Later", icon: Globe, category: "automatic",
    fields: [
      { key: "paypalClientId", label: "PayPal Client ID", type: "text", icon: KeyRound, placeholder: "AeA1QIZXiflr1_-r...", required: true },
      { key: "paypalClientSecret", label: "PayPal Client Secret", type: "password", icon: ShieldCheck, secretFlagKey: "hasPaypalClientSecret", required: true },
      {
        key: "paypalWebhookId", label: "PayPal Webhook ID", type: "text", icon: ShieldCheck,
        placeholder: "8PT597110X687430LKGECATA", required: true,
        hint: (
          <>
            From your PayPal app's Webhooks section ·{" "}
            <Link to="/paypal-keys-guide" target="_blank" className="font-semibold text-hotpink hover:underline">
              Where do I find my keys? →
            </Link>
          </>
        ),
      },
    ],
  },
  {
    key: "upi", label: "UPI ID", desc: "Direct UPI payouts", icon: AtSign, category: "manual",
    fields: [
      { key: "upiId", label: "Your UPI ID", type: "text", icon: AtSign, placeholder: "aarav@okhdfcbank", required: true, hint: "Format: name@bank" },
    ],
  },
  {
    key: "qr", label: "QR Code", desc: "Static QR for scan & pay", icon: QrCode, category: "manual",
    fields: [{ key: "qrImage", label: "QR image", type: "image", required: true }],
  },
  {
    key: "bank", label: "Bank Transfer", desc: "IMPS / NEFT / RTGS", icon: Landmark, category: "manual",
    fields: [
      { key: "bankName", label: "Bank name", type: "text", icon: Landmark, placeholder: "HDFC Bank", required: true },
      { key: "ifsc", label: "IFSC code", type: "text", placeholder: "HDFC0001234", required: true, uppercase: true },
      { key: "payee", label: "Payee name", type: "text", placeholder: "Aarav Sharma", required: true },
      { key: "accountNumber", label: "Account number", type: "text", placeholder: "1234567890123", required: true, digitsOnly: true },
      { key: "bankInstructions", label: "Custom instructions", type: "textarea", hint: "Shown to buyers on the payment screen", rows: 2 },
    ],
  },
];

// PayPal can only receive certain currencies. Right now your COUNTRIES list
// only ever produces INR or USD, so this is a 1-item allowlist today — but
// keeping it as a list means adding EUR/GBP/etc. later is a one-line change.
const PAYPAL_SUPPORTED_CURRENCIES = ["USD"];

const FAQ_QUESTION_LIMIT = 50;
const FAQ_ANSWER_LIMIT = 120;
const FAQ_SLOTS = 5;
const emptyFaqs = () => Array.from({ length: FAQ_SLOTS }, () => ({ question: "", answer: "" }));

// One distinct example per slot, purely as placeholder text — each shows the
// merchant a different kind of question so the 5 slots don't all look like
// copies of the same idea. Real questions/answers are never pre-filled.
const FAQ_PLACEHOLDERS = [
  { question: "Do you accept COD?", answer: "Yes, COD is available on orders under \u20b95000 in most pin codes." },
  { question: "How long does shipping take?", answer: "Orders ship within 1-2 days and usually arrive in 4-6 business days." },
  { question: "How do I know my size?", answer: "Check the size chart in our photos, or DM your height & usual size and we'll suggest one." },
  { question: "Can I exchange for a different size?", answer: "Yes, free exchanges within 7 days as long as the item is unused and tags are intact." },
  { question: "Is this item in stock right now?", answer: "If it's listed on our page it's in stock — DM the color/size you want and we'll confirm." },
];

// Three ready-made review-request templates. Picking one overwrites both the
// subject and body fields — merchant can still edit freely after choosing.
const REVIEW_REQUEST_TEMPLATES = [
  {
    key: "review_discount",
    label: "Review + Discount",
    subject: "How was your order from {{business_name}}?",
    body:
      "Hi {{customer_name}},\n\nHope you're loving your order ({{order_number}})!\n\n{{items_text}}\n\n" +
      "If you have a minute, we'd really appreciate a review on our Instagram post or Story — just tag us " +
      "or send us a screenshot!\n\nAs a thank you, here's 5% OFF your next order (valid for 1 month) — " +
      "reply to this email or DM us on Instagram and we'll send you a code.\n\n-- {{business_name}}",
  },
  {
    key: "simple_thanks",
    label: "Simple Thank You",
    subject: "Thanks for shopping with {{business_name}}!",
    body:
      "Hi {{customer_name}},\n\nJust wanted to say thank you for ordering ({{order_number}}) from us!\n\n" +
      "{{items_text}}\n\nIf you loved what you got, we'd be so grateful if you shared a quick photo or " +
      "Story tagging us on Instagram — it means the world to a small business like ours.\n\n" +
      "See you again soon!\n\n-- {{business_name}}",
  },
  {
    key: "vip_perk",
    label: "VIP Perk",
    subject: "You're on the list, {{customer_name}} \ud83c\udf89",
    body:
      "Hi {{customer_name}},\n\nHope your order ({{order_number}}) arrived just right!\n\n{{items_text}}\n\n" +
      "Tag us in a post or Story and we'll add you to our VIP list — first access to new drops and " +
      "restocks, before anyone else.\n\nJust reply to this email or DM us on Instagram once you've posted.\n\n" +
      "-- {{business_name}}",
  },
];

export default function ProfileInfo() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    countryCode: "IN",
    dial: "+91",
    phone: "",
    address: "",
    bio: "",
    instagramUrl: "",
    facebookUrl: "",
    youtubeUrl: "",
    websiteUrl: "",
    logoImage: null,
    showPhoneInFooter: false,
    businessRegistrationNumber: "",
    footerTagline: "",
    payMethod: "razorpay",
    razorpayId: "",
    razorpaySecret: "",
    razorpayWebhookSecret: "",
    stripeSecretKey: "",
    stripeWebhookSecret: "",
    paypalClientId: "",
    paypalClientSecret: "",
    paypalWebhookId: "",
    upiId: "",
    qrImage: null,
    bankName: "",
    ifsc: "",
    payee: "",
    accountNumber: "",
    bankInstructions: "",
    returnsEnabled: true,
    exchangesEnabled: false,
    returnPolicy: "",
    returnsDisabledMessage: "",
    returnWindowDays: "",
    exchangePolicy: "",
    abandonedCartEnabled: true,
    abandonedCartNudge1Minutes: "",
    abandonedCartNudge2Minutes: "",
    lowStockAlertsEnabled: true,
    catalogRandomizeEnabled: false,
    crossSellEnabled: true,
    unshippedRemindersEnabled: true,
    unshippedReminderHours: "",
    reviewRequestEnabled: false,
    reviewRequestDelayDays: "",
    reviewRequestEmailSubject: "",
    reviewRequestEmailBody: "",
    faqs: emptyFaqs(),
  });
  const [plan, setPlan] = useState(null);
  const abandonedCartAvailable = plan === "growth" || plan === "pro";
  const lowStockAlertsAvailable = plan === "growth" || plan === "pro";
  const shippingRemindersAvailable = !!plan && plan !== "free" && plan !== "creator";
  const reviewRequestAvailable = plan === "growth" || plan === "pro";
  const socialLinksAvailable = plan === "growth" || plan === "pro";
  const footerExtrasAvailable = plan === "pro";
  const crossSellAvailable = plan === "pro";
  const catalogRandomizeAvailable = plan === "pro";
  const firstName = form.name.trim().split(/\s+/)[0] || "";

  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasRazorpaySecret, setHasRazorpaySecret] = useState(false);
  const [hasStripeSecret, setHasStripeSecret] = useState(false);
  const [hasStripeWebhookSecret, setHasStripeWebhookSecret] = useState(false);
  const [hasPaypalClientSecret, setHasPaypalClientSecret] = useState(false);
  const [hasRazorpayWebhookSecret, setHasRazorpayWebhookSecret] = useState(false);
  const [qrUploading, setQrUploading] = useState(false);
  const [faqOpen, setFaqOpen] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(true);
  const [reviewRequestOpen, setReviewRequestOpen] = useState(true);
  const [socialLinksOpen, setSocialLinksOpen] = useState(true);
  const [footerBrandingOpen, setFooterBrandingOpen] = useState(true);
  const qrRef = useRef(null);

  useEffect(() => {
    api
      .get("/profile")
      .then(({ data }) => {
        const country = COUNTRIES.find((c) => c.currency === data.currency) || COUNTRIES[0];
        const loadedFaqs = emptyFaqs();
        (data.faqs || []).slice(0, FAQ_SLOTS).forEach((f, i) => {
          loadedFaqs[i] = { question: f.question || "", answer: f.answer || "" };
        });
        // Merchants fill these in once and rarely revisit — start collapsed
        // if there's already at least one saved pair, so the profile page
        // isn't a mile of scrolling on a phone. First-time setup (nothing
        // saved yet) starts open so it isn't hidden from a new merchant.
        setFaqOpen(loadedFaqs.every((f) => !f.question.trim() && !f.answer.trim()));

        // Same idea for Payment Methods (required) and Review Request
        // (optional): collapsed if what's saved already satisfies the
        // required fields, open if there's still setup to do.
        const loadedMethod = PAYMENT_METHODS.find((m) => m.key === (data.payment_method || "razorpay"));
        const savedSecretFlags = {
          hasRazorpaySecret: !!data.has_razorpay_secret,
          hasRazorpayWebhookSecret: !!data.has_razorpay_webhook_secret,
          hasStripeSecret: !!data.has_stripe_secret,
          hasStripeWebhookSecret: !!data.has_stripe_webhook_secret,
          hasPaypalClientSecret: !!data.has_paypal_client_secret,
        };
        const loadedFieldValue = {
          razorpayId: data.razorpay_key_id,
          paypalClientId: data.paypal_client_id,
          paypalWebhookId: data.paypal_webhook_id,
          upiId: data.upi_id,
          qrImage: data.qr_code_image_url,
          bankName: data.bank_name,
          ifsc: data.bank_ifsc,
          payee: data.bank_account_name,
          accountNumber: data.bank_account_number,
        };
        const paymentComplete = loadedMethod
          ? loadedMethod.fields.every((f) => {
              if (!f.required) return true;
              if (f.secretFlagKey && savedSecretFlags[f.secretFlagKey]) return true;
              const val = loadedFieldValue[f.key];
              return f.type === "image" ? !!val : !!(val && val.toString().trim());
            })
          : false;
        setPaymentOpen(!paymentComplete);

        const reviewComplete =
          !(data.review_request_enabled ?? false) ||
          (data.review_request_delay_days != null &&
            !!data.review_request_email_subject &&
            !!data.review_request_email_body);
        setReviewRequestOpen(!reviewComplete);

        const socialFilled = !!(data.instagram_url || data.facebook_url || data.youtube_url);
        setSocialLinksOpen(!socialFilled);

        const footerFilled = !!(
          data.website_url ||
          data.logo_image_url ||
          data.footer_tagline ||
          data.business_registration_number ||
          data.show_phone_in_footer
        );
        setFooterBrandingOpen(!footerFilled);

        setForm((f) => ({
          ...f,
          name: data.contact_name || "",
          email: data.contact_email || "",
          phone: (data.contact_phone || "").replace(/^(\+\d{1,4}\s+)+/, ""),
          address: data.contact_address || "",
          bio: data.bio || "",
          instagramUrl: data.instagram_url || "",
          facebookUrl: data.facebook_url || "",
          youtubeUrl: data.youtube_url || "",
          websiteUrl: data.website_url || "",
          logoImage: data.logo_image_url ? { name: "Logo", url: data.logo_image_url } : null,
          showPhoneInFooter: data.show_phone_in_footer ?? false,
          businessRegistrationNumber: data.business_registration_number || "",
          footerTagline: data.footer_tagline || "",
          countryCode: data.country || country.code,
          dial: (COUNTRIES.find((c) => c.code === data.country) || country).dial,
          payMethod: data.payment_method || "razorpay",
          razorpayId: data.razorpay_key_id || "",
          paypalClientId: data.paypal_client_id || "",
          paypalWebhookId: data.paypal_webhook_id || "",
          upiId: data.upi_id || "",
          qrImage: data.qr_code_image_url ? { name: "QR code", url: data.qr_code_image_url } : null,
          bankName: data.bank_name || "",
          ifsc: data.bank_ifsc || "",
          payee: data.bank_account_name || "",
          accountNumber: data.bank_account_number || "",
          bankInstructions: data.bank_instructions || "",
          returnsEnabled: data.returns_enabled ?? true,
          exchangesEnabled: data.exchanges_enabled ?? false,
          returnPolicy: data.return_policy || "",
          returnsDisabledMessage: data.returns_disabled_message || "",
          returnWindowDays: data.return_window_days != null ? String(data.return_window_days) : "",
          exchangePolicy: data.exchange_policy || "",
          abandonedCartEnabled: data.abandoned_cart_enabled ?? true,
          abandonedCartNudge1Minutes:
            data.abandoned_cart_nudge1_minutes != null ? String(data.abandoned_cart_nudge1_minutes) : "60",
          abandonedCartNudge2Minutes:
            data.abandoned_cart_nudge2_minutes != null ? String(data.abandoned_cart_nudge2_minutes) : "1200",
          lowStockAlertsEnabled: data.low_stock_alerts_enabled ?? true,
          catalogRandomizeEnabled: data.catalog_randomize_enabled ?? false,
          crossSellEnabled: data.cross_sell_enabled ?? false,
          unshippedRemindersEnabled: data.unshipped_reminders_enabled ?? true,
          unshippedReminderHours:
            data.unshipped_reminder_hours != null ? String(data.unshipped_reminder_hours) : "24",
          reviewRequestEnabled: data.review_request_enabled ?? false,
          reviewRequestDelayDays:
            data.review_request_delay_days != null ? String(data.review_request_delay_days) : "7",
          reviewRequestEmailSubject: data.review_request_email_subject || "",
          reviewRequestEmailBody: data.review_request_email_body || "",
          faqs: loadedFaqs,
        }));
        setHasRazorpaySecret(!!data.has_razorpay_secret);
        setHasStripeSecret(!!data.has_stripe_secret);
        setHasStripeWebhookSecret(!!data.has_stripe_webhook_secret);
        setHasPaypalClientSecret(!!data.has_paypal_client_secret);
        setHasRazorpayWebhookSecret(!!data.has_razorpay_webhook_secret);
        setPlan(data.plan || null);
      })
      .catch(() => toast.error("Couldn't load your profile. Try refreshing."))
      .finally(() => setLoading(false));
  }, []);

    const activeCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === form.countryCode) || COUNTRIES[0],
    [form.countryCode]
  );

  const paypalUnsupported = !PAYPAL_SUPPORTED_CURRENCIES.includes(activeCountry.currency);

  const faqFilledCount = useMemo(
    () => form.faqs.filter((f) => f.question.trim() && f.answer.trim()).length,
    [form.faqs]
  );

  const paymentConfigured = useMemo(() => {
    const activeMethod = PAYMENT_METHODS.find((m) => m.key === form.payMethod);
    if (!activeMethod) return false;
    return activeMethod.fields.every((f) => {
      if (!f.required) return true;
      const hasSaved = f.secretFlagKey
        ? { hasRazorpaySecret, hasRazorpayWebhookSecret, hasStripeSecret, hasStripeWebhookSecret, hasPaypalClientSecret }[f.secretFlagKey]
        : false;
      const val = form[f.key];
      const filled = f.type === "image" ? !!val : (val || "").toString().trim();
      return !!filled || hasSaved;
    });
  }, [
    form.payMethod, form.razorpayId, form.razorpaySecret, form.razorpayWebhookSecret,
    form.stripeSecretKey, form.stripeWebhookSecret,
    form.paypalClientId, form.paypalClientSecret, form.paypalWebhookId,
    form.upiId, form.qrImage, form.bankName, form.ifsc, form.payee, form.accountNumber,
    hasRazorpaySecret, hasRazorpayWebhookSecret, hasStripeSecret, hasStripeWebhookSecret, hasPaypalClientSecret,
  ]);

  // Explicit "off" counts as configured too — nothing pending to fill in.
  const reviewRequestConfigured = useMemo(() => {
    if (!form.reviewRequestEnabled) return true;
    const days = Number(form.reviewRequestDelayDays);
    return (
      !!form.reviewRequestDelayDays && days >= 1 && days <= 90 &&
      !!form.reviewRequestEmailSubject.trim() &&
      !!form.reviewRequestEmailBody.trim()
    );
  }, [form.reviewRequestEnabled, form.reviewRequestDelayDays, form.reviewRequestEmailSubject, form.reviewRequestEmailBody]);

  const socialLinksFilled = useMemo(
    () => !!(form.instagramUrl.trim() || form.facebookUrl.trim() || form.youtubeUrl.trim()),
    [form.instagramUrl, form.facebookUrl, form.youtubeUrl]
  );

  const footerBrandingFilled = useMemo(
    () =>
      !!(
        form.websiteUrl.trim() ||
        form.logoImage ||
        form.footerTagline.trim() ||
        form.businessRegistrationNumber.trim() ||
        form.showPhoneInFooter
      ),
    [form.websiteUrl, form.logoImage, form.footerTagline, form.businessRegistrationNumber, form.showPhoneInFooter]
  );

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    if (saved) setSaved(false);
  };

  // Turning the review-request toggle on should hand the merchant real,
  // ready-to-save text — not just a gray placeholder they have to type over.
  // Only fills in a default if both fields are still empty, so it never
  // clobbers something the merchant already wrote or loaded from a save.
  const applyReviewRequestDefaults = () => {
    setForm((f) => {
      const alreadyHasCopy = f.reviewRequestEmailSubject.trim() || f.reviewRequestEmailBody.trim();
      return {
        ...f,
        reviewRequestEnabled: true,
        ...(alreadyHasCopy
          ? {}
          : {
              reviewRequestEmailSubject: REVIEW_REQUEST_TEMPLATES[0].subject,
              reviewRequestEmailBody: REVIEW_REQUEST_TEMPLATES[0].body,
            }),
      };
    });
    if (errors.reviewRequestDelayDays || errors.reviewRequestEmailSubject || errors.reviewRequestEmailBody) {
      setErrors((e) => ({
        ...e,
        reviewRequestDelayDays: undefined,
        reviewRequestEmailSubject: undefined,
        reviewRequestEmailBody: undefined,
      }));
    }
    if (saved) setSaved(false);
  };

  const setFaqField = (index, field, value) => {
    setForm((f) => {
      const faqs = f.faqs.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      return { ...f, faqs };
    });
    const errKey = `faq_${index}_${field}`;
    if (errors[errKey]) setErrors((e) => ({ ...e, [errKey]: undefined }));
    if (saved) setSaved(false);
  };

  const clearFaqRow = (index) => {
    setForm((f) => {
      const faqs = f.faqs.map((row, i) => (i === index ? { question: "", answer: "" } : row));
      return { ...f, faqs };
    });
  };

    const pickQr = async (files) => {
    const f = files?.[0];
    if (!f) return;
    // Local preview immediately, then swap in the real hosted URL once the
    // upload + server-side resize/compress finishes.
    set("qrImage", { name: f.name, url: URL.createObjectURL(f) });
    setQrUploading(true);
    try {
      const { data } = await api.post("/profile/qr-upload", f, {
        headers: { "Content-Type": f.type },
      });
      set("qrImage", { name: f.name, url: data.url });
      toast.success("QR code uploaded.");
    } catch {
      toast.error("QR upload failed. Try a different image.");
    } finally {
      setQrUploading(false);
    }
  };

  const logoRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const pickLogo = async (files) => {
    const f = files?.[0];
    if (!f) return;
    set("logoImage", { name: f.name, url: URL.createObjectURL(f) });
    setLogoUploading(true);
    try {
      const { data } = await api.post("/profile/logo-upload", f, {
        headers: { "Content-Type": f.type },
      });
      set("logoImage", { name: f.name, url: data.url });
      toast.success("Logo uploaded.");
    } catch (err) {
      const code = err?.response?.data?.error;
      const message =
        code === 'pro_plan_required' ? "Logo upload is a Pro-only feature." :
        code === 'owner_only' ? "Only the account owner can upload a logo." :
        code === 'file_too_large' ? "That image is over 5 MB — try a smaller one." :
        code === 'unsupported_image_type' ? "Use a PNG, JPG, or WEBP file." :
        "Logo upload failed. Try a different image.";
      toast.error(message);
    } finally {
      setLogoUploading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your full name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Enter a valid email.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      e.phone = "Enter a valid phone number.";
    if (!form.countryCode) e.countryCode = "Choose your country.";
        const addressIssue = findAddressViolation(form.address);
    if (addressIssue) e.address = addressIssue;

    // payment fields — driven by PAYMENT_METHODS config, works for any method
    const activeMethod = PAYMENT_METHODS.find((m) => m.key === form.payMethod);
    if (activeMethod) {
      activeMethod.fields.forEach((f) => {
        if (!f.required) return;
        const hasSaved = f.secretFlagKey
          ? { hasRazorpaySecret, hasRazorpayWebhookSecret, hasStripeSecret, hasStripeWebhookSecret, hasPaypalClientSecret }[f.secretFlagKey]
          : false;
        const val = form[f.key];
        const filled = f.type === "image" ? !!val : (val || "").toString().trim();
        if (!filled && !hasSaved) e[f.key] = "Required.";
      });
      if (form.payMethod === "upi" && form.upiId && !/^[\w.\-]+@[\w.\-]+$/.test(form.upiId.trim())) {
        e.upiId = "UPI ID looks like name@bank.";
      }
      if (form.payMethod === "paypal" && paypalUnsupported) {
        e.payMethod = `PayPal doesn't support ${activeCountry.currency} for sellers in ${activeCountry.name}.`;
      }
    }
    if (plan !== "creator") {
      if (form.returnsEnabled) {
        if (!form.returnPolicy.trim()) e.returnPolicy = "Please add a policy — buyers see this before ordering.";
      } else {
        if (!form.returnsDisabledMessage.trim()) e.returnsDisabledMessage = "Let buyers know how to reach you — this shows if they type RETURN.";
      }
      if (form.exchangesEnabled && !form.exchangePolicy.trim()) {
        e.exchangePolicy = "Please add an exchange policy — buyers see this before starting an exchange.";
      }
    }
    if (abandonedCartAvailable && form.abandonedCartEnabled) {
      const n1 = Number(form.abandonedCartNudge1Minutes);
      const n2 = Number(form.abandonedCartNudge2Minutes);
      if (!form.abandonedCartNudge1Minutes || n1 < 60) e.abandonedCartNudge1Minutes = "Enter 60 minutes or more — we only check for abandoned carts once an hour.";
      if (!form.abandonedCartNudge2Minutes || n2 <= n1) e.abandonedCartNudge2Minutes = "Must be later than the first reminder.";
      if (n2 > 1260) e.abandonedCartNudge2Minutes = "Keep under 21 hours — needs headroom before Instagram's 24h reply limit.";
    }
    if (shippingRemindersAvailable && form.unshippedRemindersEnabled) {
      const hours = Number(form.unshippedReminderHours);
      if (!form.unshippedReminderHours || hours < 1 || hours > 168) {
        e.unshippedReminderHours = "Enter a number of hours between 1 and 168.";
      }
    }
    if (reviewRequestAvailable && form.reviewRequestEnabled) {
      const days = Number(form.reviewRequestDelayDays);
      if (!form.reviewRequestDelayDays || days < 1 || days > 90) {
        e.reviewRequestDelayDays = "Enter a number of days between 1 and 90.";
      }
      if (!form.reviewRequestEmailSubject.trim()) {
        e.reviewRequestEmailSubject = "Add a subject line.";
      }
      if (!form.reviewRequestEmailBody.trim()) {
        e.reviewRequestEmailBody = "Add the email body — this is what the customer will read.";
      }
    }
    // A row counts as "filled" only when both question and answer are
    // present — a lone question or lone answer isn't enough to publish,
    // since the bot can't show half a pair to a customer.
    form.faqs.forEach((row, i) => {
      const q = row.question.trim();
      const a = row.answer.trim();
      if (q && !a) e[`faq_${i}_answer`] = "Add an answer, or clear the question.";
      if (a && !q) e[`faq_${i}_question`] = "Add a question, or clear the answer.";
    });
    setErrors(e);
    return e;
  };

  // Maps each validation error key to the data-testid of the field that
  // should receive focus, so we can jump the merchant straight to the
  // first thing that needs fixing.
  const FIELD_TESTID = {
    name: "profile-name",
    email: "profile-email",
    phone: "profile-phone",
    countryCode: "profile-country-trigger",
    razorpayId: "razorpayId",
    razorpaySecret: "razorpaySecret",
    razorpayWebhookSecret: "razorpayWebhookSecret",
    stripeSecretKey: "stripeSecretKey",
    stripeWebhookSecret: "stripeWebhookSecret",
    paypalClientId: "paypalClientId",
    paypalClientSecret: "paypalClientSecret",
    paypalWebhookId: "paypalWebhookId",
    upiId: "upiId",
    qrImage: "qr-dropzone",
    bankName: "bankName",
    ifsc: "ifsc",
    payee: "payee",
    accountNumber: "accountNumber",
    returnPolicy: "return-policy",
    returnsDisabledMessage: "return-policy",
    exchangePolicy: "exchange-policy",
    abandonedCartNudge1Minutes: "abandoned-cart-nudge1",
    abandonedCartNudge2Minutes: "abandoned-cart-nudge2",
    unshippedReminderHours: "unshipped-reminder-hours",
    reviewRequestDelayDays: "review-request-delay",
    reviewRequestEmailSubject: "review-request-subject",
    reviewRequestEmailBody: "review-request-body",
  };
  const FIELD_ORDER = Object.keys(FIELD_TESTID);

  const scrollToFirstError = (errs) => {
    let firstKey = FIELD_ORDER.find((k) => errs[k]);
    let testid = firstKey ? FIELD_TESTID[firstKey] : null;
    const faqKey = Object.keys(errs).find((k) => k.startsWith("faq_"));
    if (!testid && faqKey) {
      const idx = faqKey.split("_")[1];
      testid = `faq-question-${idx}`;
    }
    if (!testid) return;
    // A FAQ error can be hiding inside a collapsed panel — open it first,
    // then wait a tick for the expand animation before scrolling to it.
    if (faqKey) setFaqOpen(true);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus({ preventScroll: true });
      }
    }, faqKey ? 260 : 0);
  };

  const submit = async (evt) => {
    evt.preventDefault();
    const validationErrors = validate();
    const errorCount = Object.keys(validationErrors).length;
    if (errorCount > 0) {
      toast.error(
        errorCount === 1
          ? "Please fix 1 field before saving — it's highlighted below."
          : `Please fix ${errorCount} fields before saving — the first one is highlighted below.`
      );
      scrollToFirstError(validationErrors);
      return;
    }
    setSaving(true);
    try {
      const faqPayload = {};
      form.faqs.forEach((row, i) => {
        const q = row.question.trim();
        const a = row.answer.trim();
        faqPayload[`faq${i + 1}_question`] = q && a ? q : "";
        faqPayload[`faq${i + 1}_answer`] = q && a ? a : "";
      });
      await api.post("/profile", {
        contact_name: form.name,
        contact_email: form.email,
        contact_phone: `${form.dial} ${form.phone}`.trim(),
        contact_address: form.address,
        bio: form.bio,
                ...(socialLinksAvailable && {
          instagram_url: form.instagramUrl,
          facebook_url: form.facebookUrl,
          youtube_url: form.youtubeUrl,
        }),
        ...(footerExtrasAvailable && {
          website_url: form.websiteUrl,
          business_registration_number: form.businessRegistrationNumber,
          footer_tagline: form.footerTagline,
          show_phone_in_footer: form.showPhoneInFooter,
        }),
        payment_method: form.payMethod,
        razorpay_key_id: form.razorpayId,
        // Empty string means "leave whatever's saved untouched" server-side —
        // only send it when the merchant actually typed a new one.
        razorpay_key_secret: form.razorpaySecret,
        razorpay_webhook_secret: form.razorpayWebhookSecret,
        stripe_secret_key: form.stripeSecretKey,
        stripe_webhook_secret: form.stripeWebhookSecret,
        paypal_client_id: form.paypalClientId,
        paypal_client_secret: form.paypalClientSecret,
        paypal_webhook_id: form.paypalWebhookId,
        upi_id: form.upiId,
        bank_account_name: form.payee,
        bank_account_number: form.accountNumber,
        bank_ifsc: form.ifsc,
        bank_name: form.bankName,
        bank_instructions: form.bankInstructions,
        returns_enabled: plan === "creator" ? false : form.returnsEnabled,
        exchanges_enabled: plan === "creator" ? false : form.exchangesEnabled,
        return_policy: form.returnPolicy,
        returns_disabled_message: form.returnsDisabledMessage,
        return_window_days: form.returnWindowDays === "" ? null : Number(form.returnWindowDays),
        exchange_policy: form.exchangePolicy,
        ...(abandonedCartAvailable && {
          abandoned_cart_enabled: form.abandonedCartEnabled,
          abandoned_cart_nudge1_minutes: Number(form.abandonedCartNudge1Minutes) || 60,
          abandoned_cart_nudge2_minutes: Number(form.abandonedCartNudge2Minutes) || 1200,
        }),
        ...(lowStockAlertsAvailable && {
          low_stock_alerts_enabled: form.lowStockAlertsEnabled,
        }),
        ...(catalogRandomizeAvailable && {
          catalog_randomize_enabled: form.catalogRandomizeEnabled,
        }),
        ...(crossSellAvailable && {
          cross_sell_enabled: form.crossSellEnabled,
        }),
        ...(shippingRemindersAvailable && {
          unshipped_reminders_enabled: form.unshippedRemindersEnabled,
          unshipped_reminder_hours: Number(form.unshippedReminderHours) || 24,
        }),
        ...(reviewRequestAvailable && {
          review_request_enabled: form.reviewRequestEnabled,
          review_request_delay_days: Number(form.reviewRequestDelayDays) || 7,
          review_request_email_subject: form.reviewRequestEmailSubject,
          review_request_email_body: form.reviewRequestEmailBody,
        }),
        ...faqPayload,
      });
      if (form.razorpaySecret.trim()) setHasRazorpaySecret(true);
      if (form.razorpayWebhookSecret.trim()) setHasRazorpayWebhookSecret(true);
      if (form.stripeSecretKey.trim()) setHasStripeSecret(true);
      if (form.stripeWebhookSecret.trim()) setHasStripeWebhookSecret(true);
      if (form.paypalClientSecret.trim()) setHasPaypalClientSecret(true);
      setForm((f) => ({
        ...f,
        razorpaySecret: "",
        razorpayWebhookSecret: "",
        stripeSecretKey: "",
        stripeWebhookSecret: "",
        paypalClientSecret: "",
      }));
      // Collapse anything that's now fully filled in, so the page gets
      // shorter as the merchant completes it instead of staying maximally
      // expanded forever.
      if (paymentConfigured) setPaymentOpen(false);
      if (reviewRequestAvailable && reviewRequestConfigured) setReviewRequestOpen(false);
      if (socialLinksAvailable && socialLinksFilled) setSocialLinksOpen(false);
      if (footerExtrasAvailable && footerBrandingFilled) setFooterBrandingOpen(false);
      setSaved(true);
      toast.success("Profile saved.");
      setTimeout(() => setSaved(false), 2600);
    } catch {
      toast.error("Couldn't save your profile. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderFields = (fields) => {
    return fields.map((f) => {
      if (f.type === "image") {
        return (
          <div key={f.key} className="flex flex-col items-start gap-4 md:flex-row md:items-center md:col-span-2">
            <div
              onClick={() => !qrUploading && qrRef.current?.click()}
              className="relative grid h-40 w-40 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-2xl border-2 border-dashed border-ink/20 bg-ink/[0.02] hover:border-hotpink"
              data-testid="qr-dropzone"
            >
              {form.qrImage ? (
                <img src={form.qrImage.url} alt="QR" className="h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-ink/50">
                  <ImagePlus className="h-6 w-6" />
                  <span className="text-xs font-semibold">Upload square QR</span>
                </div>
              )}
              {qrUploading && (
                <div className="absolute inset-0 grid place-items-center bg-white/70 text-xs font-semibold text-ink">
                  Uploading…
                </div>
              )}
              <input
                ref={qrRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { pickQr(e.target.files); e.target.value = ""; }}
              />
            </div>
            <div>
              <p className="font-bold text-ink">Static QR image</p>
              <p className="mt-1 text-sm text-ink/60">
                Any UPI / Paytm / PhonePe / GPay QR works. Use a square PNG for best results.
              </p>
              <p className="mt-1 text-xs text-ink/40">
                PNG, JPG, or WEBP · up to 5 MB
              </p>
              {form.qrImage && (
                <button
                  type="button"
                  onClick={() => set("qrImage", null)}
                  className="mt-3 inline-flex items-center gap-1 rounded-full border border-ink/15 bg-white px-3 py-1 text-xs font-semibold text-ink hover:bg-ink/5"
                >
                  <X className="h-3 w-3" /> Replace
                </button>
              )}
              {errors.qrImage && <p className="mt-2 text-xs font-semibold text-hotpink">{errors.qrImage}</p>}
            </div>
          </div>
        );
      }

      const savedFlag = f.secretFlagKey
        ? { hasRazorpaySecret, hasRazorpayWebhookSecret, hasStripeSecret, hasStripeWebhookSecret, hasPaypalClientSecret }[f.secretFlagKey]
        : false;

      return (
        <Field key={f.key} label={f.label} required={f.required} error={errors[f.key]} hint={f.hint}>
          {f.type === "textarea" ? (
            <textarea
              rows={f.rows || 3}
              value={form[f.key] || ""}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid={f.key}
            />
          ) : (
            <IconInput icon={f.icon || KeyRound}>
              <input
                type={f.type === "password" ? "password" : "text"}
                value={form[f.key] || ""}
                onChange={(e) => {
                  let v = e.target.value;
                  if (f.digitsOnly) v = v.replace(/\D/g, "");
                  if (f.uppercase) v = v.toUpperCase();
                  set(f.key, v);
                }}
                placeholder={f.type === "password" && savedFlag ? "•••• saved — leave blank to keep it" : f.placeholder}
                className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                data-testid={f.key}
              />
            </IconInput>
          )}
          {f.type === "password" && savedFlag && !form[f.key] && (
            <p className="mt-1 text-xs text-green-600">A value is already saved.</p>
          )}
        </Field>
      );
    });
  };

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      <div className="mx-auto max-w-4xl">
        <BackToDashboard />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hotpink">
          Merchant profile
        </p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-ink md:text-6xl">
          Tell us who's behind the DMs.
        </h1>
        <p className="mt-4 max-w-xl text-ink/60">
          Buyers see your name and business info at checkout. Payouts land wherever you point them.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-8">
          {/* Personal */}
          <Section title="Personal details" caption="Required — buyers see this at checkout.">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Full name" required error={errors.name}>
                <IconInput icon={User}>
                  <input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Aarav Sharma"
                    className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                    data-testid="profile-name"
                  />
                </IconInput>
              </Field>

              <Field label="Email" required error={errors.email}>
                <IconInput icon={Mail}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@business.com"
                    className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                    data-testid="profile-email"
                  />
                </IconInput>
              </Field>

              <Field label="Country" hint="Contact support to change it">
                <div className="flex w-full cursor-not-allowed items-center gap-2 rounded-2xl border border-ink/10 bg-ink/[0.03] py-2.5 pl-4 pr-3 text-sm text-ink/50">
                  <Globe className="h-4 w-4 text-ink/30" />
                  {activeCountry.name} <span className="text-ink/40">· {activeCountry.currency}</span>
                  <Lock className="ml-auto h-3.5 w-3.5 text-ink/30" />
                </div>
              </Field>

              <Field label="Phone number" required error={errors.phone}>
                <div className="flex items-stretch overflow-hidden rounded-2xl border border-ink/15 bg-white focus-within:border-hotpink">
                  <div className="flex items-center gap-1.5 border-r border-ink/10 bg-ink/[0.03] px-3 text-sm font-semibold text-ink">
                    <Phone className="h-4 w-4 text-ink/40" />
                    {form.dial}
                  </div>
                  <input
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value.replace(/[^\d\s\-]/g, ""))}
                    placeholder="98765 43210"
                    className="w-full bg-transparent px-3 py-2.5 text-sm outline-none"
                    data-testid="profile-phone"
                  />
                </div>
              </Field>
            </div>
          </Section>

          {/* Address + bio */}
          <Section title="About your business" caption="Optional. Shown to customers in order-related emails, like your store's return address.">
            <Field label="Address" hint={`${form.address.length}/150 · optional`}>
              <IconInput icon={MapPin}>
                <input
                  value={form.address}
                  onChange={(e) => set("address", e.target.value.slice(0, 150))}
                  placeholder="Shop 12, Indiranagar, Bengaluru 560038"
                  className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                  data-testid="profile-address"
                />
              </IconInput>
            </Field>

            <Field label="Short bio" hint={`${form.bio.length}/240 · optional`}>
              <textarea
                rows={4}
                maxLength={240}
                value={form.bio}
                onChange={(e) => set("bio", e.target.value)}
                placeholder="A one-woman ceramics studio shipping from Panjim. Every piece is thrown, glazed and packed by me."
                className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                data-testid="profile-bio"
              />
            </Field>
          </Section>

          {/* Social links — Growth/Pro only, shown as clickable footer icons in customer emails */}
          <Section
            title="Social links"
            collapsible={socialLinksAvailable}
            open={socialLinksOpen}
            onToggle={() => setSocialLinksOpen((o) => !o)}
            badge={
              socialLinksAvailable ? (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    socialLinksFilled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                  }`}
                >
                  {socialLinksFilled ? "Added" : "Optional"}
                </span>
              ) : null
            }
            caption={
              socialLinksAvailable
                ? "Optional — shown as clickable icons in the footer of customer order emails (confirmation, shipping, returns)."
                : "Available on Growth and Pro — shows clickable Instagram/Facebook/YouTube icons in the footer of customer order emails."
            }
          >
            {!socialLinksAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} gives buyers an easy way to follow you.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">
                    Upgrade to Growth or Pro
                  </Link>{" "}
                  to turn it on.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Instagram" hint="optional">
                  <IconInput icon={Instagram}>
                    <input
                      value={form.instagramUrl}
                      onChange={(e) => set("instagramUrl", e.target.value)}
                      placeholder="https://instagram.com/yourstore"
                      className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                      data-testid="profile-instagram-url"
                    />
                  </IconInput>
                </Field>
                <Field label="Facebook" hint="optional">
                  <IconInput icon={Facebook}>
                    <input
                      value={form.facebookUrl}
                      onChange={(e) => set("facebookUrl", e.target.value)}
                      placeholder="https://facebook.com/yourstore"
                      className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                      data-testid="profile-facebook-url"
                    />
                  </IconInput>
                </Field>
                <Field label="YouTube" hint="optional">
                  <IconInput icon={Youtube}>
                    <input
                      value={form.youtubeUrl}
                      onChange={(e) => set("youtubeUrl", e.target.value)}
                      placeholder="https://youtube.com/@yourstore"
                      className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                      data-testid="profile-youtube-url"
                    />
                  </IconInput>
                </Field>
              </div>
            )}
          </Section>

          {/* Footer & branding — PRO only */}
          <Section
            title="Footer & branding"
            collapsible={footerExtrasAvailable}
            open={footerBrandingOpen}
            onToggle={() => setFooterBrandingOpen((o) => !o)}
            badge={
              footerExtrasAvailable ? (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    footerBrandingFilled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                  }`}
                >
                  {footerBrandingFilled ? "Added" : "Optional"}
                </span>
              ) : null
            }
            caption={
              footerExtrasAvailable
                ? "Optional — shown in the footer of customer order emails, alongside your existing store name and address."
                : "Available on Pro — add your website, logo, business registration number, and a tagline to the footer of customer order emails."
            }
          >
            {!footerExtrasAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} gives your emails a more branded, trustworthy look.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">
                    Upgrade to Pro
                  </Link>{" "}
                  to turn it on.
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
                  <div
                    onClick={() => !logoUploading && logoRef.current?.click()}
                    className="relative grid h-28 w-28 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-2xl border-2 border-dashed border-ink/20 bg-ink/[0.02] hover:border-hotpink"
                  >
                    {form.logoImage ? (
                      <img src={form.logoImage.url} alt="Logo" className="h-full w-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-ink/50">
                        <ImagePlus className="h-6 w-6" />
                        <span className="text-xs font-semibold">Upload logo</span>
                      </div>
                    )}
                    {logoUploading && (
                      <div className="absolute inset-0 grid place-items-center bg-white/70 text-xs font-semibold text-ink">
                        Uploading…
                      </div>
                    )}
                    <input
                      ref={logoRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => { pickLogo(e.target.files); e.target.value = ""; }}
                    />
                  </div>
                  <div>
                    <p className="font-bold text-ink">Brand logo</p>
                    <p className="mt-1 text-sm text-ink/60">
                      Shown next to your store name — doesn't replace it. Square PNG works best.
                    </p>
                    <p className="mt-1 text-xs text-ink/40">
                      PNG, JPG, or WEBP · up to 5 MB
                    </p>
                    {form.logoImage && (
                      <button
                        type="button"
                        onClick={() => set("logoImage", null)}
                        className="mt-3 inline-flex items-center gap-1 rounded-full border border-ink/15 bg-white px-3 py-1 text-xs font-semibold text-ink hover:bg-ink/5"
                      >
                        <X className="h-3 w-3" /> Replace
                      </button>
                    )}
                  </div>
                </div>

                <Field label="Website" hint="optional · shown as a link in the footer">
                  <IconInput icon={Link2}>
                    <input
                      value={form.websiteUrl}
                      onChange={(e) => set("websiteUrl", e.target.value)}
                      placeholder="https://yourstore.com"
                      className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                      data-testid="profile-website-url"
                    />
                  </IconInput>
                </Field>

                <Field label="Brand tagline" hint={`${form.footerTagline.length}/100 · optional`}>
                  <input
                    value={form.footerTagline}
                    onChange={(e) => set("footerTagline", e.target.value.slice(0, 100))}
                    placeholder="Handmade ceramics, shipped with care"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    data-testid="profile-footer-tagline"
                  />
                </Field>

                <Field label="Business registration / VAT number" hint="optional">
                  <IconInput icon={Building2}>
                    <input
                      value={form.businessRegistrationNumber}
                      onChange={(e) => set("businessRegistrationNumber", e.target.value.slice(0, 60))}
                      placeholder="GSTIN / VAT / registration number"
                      className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                      data-testid="profile-registration-number"
                    />
                  </IconInput>
                </Field>

                <label className="flex items-center gap-3 rounded-2xl border border-ink/15 bg-white p-4">
                  <input
                    type="checkbox"
                    checked={form.showPhoneInFooter}
                    onChange={(e) => set("showPhoneInFooter", e.target.checked)}
                    className="h-4 w-4"
                    data-testid="profile-show-phone-footer"
                  />
                  <span className="text-sm">
                    <span className="block font-bold text-ink">Show my phone number in the footer</span>
                    <span className="block text-xs text-ink/50">Uses the phone number from Personal details above.</span>
                  </span>
                </label>
              </>
            )}
          </Section>

          {/* Payout method */}
          <Section
            title="How would you like to be paid?"
            caption="Payouts go directly to you. CheckoutDM never touches the money. Pick exactly one — tap a method below to set it up. Automatic methods verify payment instantly; manual methods need you to confirm payment yourself."
            collapsible
            open={paymentOpen}
            onToggle={() => setPaymentOpen((o) => !o)}
            badge={
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                  paymentConfigured ? "bg-mint text-ink" : "bg-hotpink/10 text-hotpink"
                }`}
              >
                {paymentConfigured ? "Set up" : "Needs setup"}
              </span>
            }
          >
            {["automatic", "manual"].map((cat) => (
              <div key={cat} className="mb-6 last:mb-0">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">
                  {cat === "automatic" ? "Automatic — verified instantly" : "Manual — you confirm payment yourself"}
                </p>
                                <div className="space-y-2">
                  {PAYMENT_METHODS.filter((m) => m.category === cat).map((m) => {
                    const Icon = m.icon;
                    const active = form.payMethod === m.key;
                    const locked = m.key === "paypal" && paypalUnsupported;

                    if (locked) {
                      return (
                        <div
                          key={m.key}
                          className="flex items-center gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4"
                        >
                          <Icon className="h-5 w-5 shrink-0 text-ink/30" />
                          <span className="flex-1">
                            <span className="block text-sm font-bold text-ink/50">{m.label}</span>
                            <span className="block text-xs text-ink/40">
                              Your country ({activeCountry.name}) doesn't support accepting payments
                              in {activeCountry.currency} via PayPal.
                            </span>
                          </span>
                          <Lock className="h-4 w-4 shrink-0 text-ink/30" />
                        </div>
                      );
                    }

                    return (
                      <div key={m.key} className={`overflow-hidden rounded-2xl border transition-colors ${active ? "border-ink" : "border-ink/10"}`}>
                        <button
                          type="button"
                          onClick={() => set("payMethod", m.key)}
                          data-testid={`pay-${m.key}`}
                          className={`flex w-full items-center gap-3 p-4 text-left ${active ? "bg-ink text-cream" : "bg-white text-ink hover:bg-ink/[0.02]"}`}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          <span className="flex-1">
                            <span className="block text-sm font-bold">{m.label}</span>
                            <span className={`block text-xs ${active ? "text-cream/60" : "text-ink/50"}`}>{m.desc}</span>
                          </span>
                          {active && <Check className="h-4 w-4 shrink-0" />}
                          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${active ? "rotate-180" : ""} ${active ? "text-cream/60" : "text-ink/30"}`} />
                        </button>
                        <AnimatePresence initial={false}>
                          {active && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden bg-white"
                            >
                              <div className="grid gap-4 border-t border-ink/10 p-5 md:grid-cols-2">
                                {renderFields(m.fields)}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </Section>

          {plan === "creator" ? (
            <Section
              title="Returns & exchanges"
              caption="Not available on the Creator plan."
            >
              <div className="flex items-start gap-3 rounded-2xl border border-dashed border-ink/15 bg-ink/[0.02] px-4 py-4">
                <Lock className="mt-0.5 h-4 w-4 flex-none text-ink/40" />
                <p className="text-sm text-ink/60">
                  Creator sells digital items only, which are delivered instantly — so returns and exchanges don't apply.
                </p>
              </div>
            </Section>
          ) : (
          <>
          {/* Returns */}
          <Section
            title="Returns & refunds"
            caption="Required — shown to buyers before they order, and used by the Return flow in DMs."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => set("returnsEnabled", true)}
                data-testid="returns-yes"
                className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                  form.returnsEnabled
                    ? "border-ink bg-ink text-cream"
                    : "border-ink/15 bg-white text-ink hover:border-ink/30"
                }`}
              >
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                    form.returnsEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                  }`}
                >
                  <Check className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-bold">We accept returns</span>
                  <span className={`block text-xs ${form.returnsEnabled ? "text-cream/70" : "text-ink/50"}`}>
                    Buyers can type RETURN in DM
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => set("returnsEnabled", false)}
                data-testid="returns-no"
                className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                  !form.returnsEnabled
                    ? "border-ink bg-ink text-cream"
                    : "border-ink/15 bg-white text-ink hover:border-ink/30"
                }`}
              >
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                    !form.returnsEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"
                  }`}
                >
                  <X className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-bold">No returns</span>
                  <span className={`block text-xs ${!form.returnsEnabled ? "text-cream/70" : "text-ink/50"}`}>
                    Final sale, no exceptions
                  </span>
                </span>
              </button>
            </div>

            {(form.returnsEnabled || form.exchangesEnabled) && (
              <Field
                label="Return & Exchange Window"
                hint="Days after the order date a return or exchange can be requested — leave blank to use the default of 7. This window is shared by Exchanges below too."
              >
                <input
                  type="number"
                  min={0}
                  max={90}
                  value={form.returnWindowDays}
                  onChange={(e) => set("returnWindowDays", e.target.value.replace(/[^\d]/g, "").slice(0, 2))}
                  placeholder="7"
                  className="w-40 rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="return-window-days"
                />
              </Field>
            )}

            <Field
              label={form.returnsEnabled ? "Return policy" : "Message for customers"}
              required
              error={form.returnsEnabled ? errors.returnPolicy : errors.returnsDisabledMessage}
              hint={
                form.returnsEnabled
                  ? `${form.returnPolicy.length}/200`
                  : `${form.returnsDisabledMessage.length}/300 · shown when a buyer types RETURN`
              }
            >
              {form.returnsEnabled ? (
                <textarea
                  rows={3}
                  value={form.returnPolicy}
                  onChange={(e) => set("returnPolicy", e.target.value.slice(0, 200))}
                  placeholder="We offer a 7 day return policy. Please type RETURN in the chat to initiate a return."
                  className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="return-policy"
                />
              ) : (
                <textarea
                  rows={3}
                  value={form.returnsDisabledMessage}
                  onChange={(e) => set("returnsDisabledMessage", e.target.value.slice(0, 300))}
                  placeholder="We don't accept returns, but if your order arrived damaged or wrong, email us at you@business.com and we'll sort it out."
                  className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="return-policy"
                />
              )}
            </Field>
          </Section>

          {/* Exchanges */}
          <Section
            title="Exchanges"
            caption="Optional — lets buyers swap an item for a different size at no extra cost, via the Exchange flow in DMs. Uses the same return window set above."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => set("exchangesEnabled", true)}
                data-testid="exchanges-yes"
                className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                  form.exchangesEnabled
                    ? "border-ink bg-ink text-cream"
                    : "border-ink/15 bg-white text-ink hover:border-ink/30"
                }`}
              >
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${form.exchangesEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"}`}>
                  <Check className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-bold">We accept exchanges</span>
                  <span className={`block text-xs ${form.exchangesEnabled ? "text-cream/70" : "text-ink/50"}`}>
                    Buyers can type EXCHANGE for a different size
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => set("exchangesEnabled", false)}
                data-testid="exchanges-no"
                className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                  !form.exchangesEnabled
                    ? "border-ink bg-ink text-cream"
                    : "border-ink/15 bg-white text-ink hover:border-ink/30"
                }`}
              >
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${!form.exchangesEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"}`}>
                  <X className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-bold">No exchanges</span>
                  <span className={`block text-xs ${!form.exchangesEnabled ? "text-cream/70" : "text-ink/50"}`}>
                    Same size or nothing
                  </span>
                </span>
              </button>
            </div>

            {form.exchangesEnabled && (
              <>
                <p className="rounded-xl bg-ink/[0.03] px-3 py-2 text-xs text-ink/50">
                  Uses the same {form.returnWindowDays || 7}-day window set in Returns &amp; refunds above.
                </p>
                <Field
                  label="Exchange policy"
                  required
                  error={errors.exchangePolicy}
                  hint={`${form.exchangePolicy.length}/200`}
                >
                  <textarea
                    rows={3}
                    value={form.exchangePolicy}
                    onChange={(e) => set("exchangePolicy", e.target.value.slice(0, 200))}
                    placeholder="We offer free exchanges within 7 days as long as the item is unused with tags intact. Type EXCHANGE in the chat to start one."
                    className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    data-testid="exchange-policy"
                  />
                </Field>
              </>
            )}
          </Section>
          </>
          )}

          {/* Frequently Asked Questions — up to 5, view-only for customers.
              Collapsible: merchants set this up once and may not touch it
              again for months, so we don't force a long panel to stay open
              and eat up scroll space on small screens. */}
          <div className="rounded-3xl border border-ink/10 bg-white p-6 md:p-8">
            <button
              type="button"
              onClick={() => setFaqOpen((o) => !o)}
              aria-expanded={faqOpen}
              data-testid="faq-panel-toggle"
              className="flex w-full items-start justify-between gap-4 text-left"
            >
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold text-ink">Frequently asked questions</h2>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      faqFilledCount > 0 ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    {faqFilledCount}/{FAQ_SLOTS} added
                  </span>
                </div>
                <p className="mt-1 max-w-2xl text-sm text-ink/50">
                  Optional — a customer who types <span className="font-semibold text-ink">FAQ</span> in
                  DM sees everything below as a plain list, nothing to select. Set it up once; it won't
                  need touching again unless something changes.
                </p>
              </div>
              <span className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink/10 bg-ink/[0.03] text-ink/60">
                <ChevronDown className={`h-4 w-4 transition-transform ${faqOpen ? "rotate-180" : ""}`} />
              </span>
            </button>

            <AnimatePresence initial={false}>
              {faqOpen && (
                <motion.div
                  key="faq-content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="mt-6 space-y-4">
                    {form.faqs.map((row, i) => {
                      const qErr = errors[`faq_${i}_question`];
                      const aErr = errors[`faq_${i}_answer`];
                      const filled = row.question.trim() && row.answer.trim();
                      const hasContent = row.question.trim() || row.answer.trim();
                      const ph = FAQ_PLACEHOLDERS[i] || FAQ_PLACEHOLDERS[0];
                      return (
                        <div
                          key={i}
                          className={`rounded-2xl border p-4 transition-colors ${
                            filled ? "border-mint/60 bg-mint/[0.06]" : "border-ink/10 bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/60">
                              <span
                                className={`grid h-5 w-5 place-items-center rounded-full text-[10px] font-black ${
                                  filled ? "bg-mint text-ink" : "bg-hotpink/10 text-hotpink"
                                }`}
                              >
                                {filled ? <Check className="h-3 w-3" /> : i + 1}
                              </span>
                              FAQ {i + 1}
                            </p>
                            {hasContent && (
                              <button
                                type="button"
                                onClick={() => clearFaqRow(i)}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink/40 hover:text-hotpink"
                              >
                                <Trash2 className="h-3 w-3" /> Clear
                              </button>
                            )}
                          </div>
                          <div className="mt-3 grid gap-3">
                            <Field
                              label="Question"
                              error={qErr}
                              hint={`${row.question.length}/${FAQ_QUESTION_LIMIT}`}
                            >
                              <input
                                value={row.question}
                                onChange={(e) => setFaqField(i, "question", e.target.value.slice(0, FAQ_QUESTION_LIMIT))}
                                placeholder={ph.question}
                                className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                                data-testid={`faq-question-${i}`}
                              />
                            </Field>
                            <Field
                              label="Answer"
                              error={aErr}
                              hint={`${row.answer.length}/${FAQ_ANSWER_LIMIT}`}
                            >
                              <textarea
                                rows={2}
                                value={row.answer}
                                onChange={(e) => setFaqField(i, "answer", e.target.value.slice(0, FAQ_ANSWER_LIMIT))}
                                placeholder={ph.answer}
                                className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                                data-testid={`faq-answer-${i}`}
                              />
                            </Field>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Live preview of exactly what the customer sees on DM */}
                  <div className="mt-6 rounded-2xl bg-ink/[0.03] p-4">
                    <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-ink/40">
                      <MessageCircle className="h-3 w-3" /> What the customer sees when they type FAQ
                    </p>
                    <div className="mt-2 max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 text-xs leading-relaxed text-ink shadow-sm">
                      {faqFilledCount === 0 ? (
                        <span className="text-ink/60">
                          We haven't added any FAQs yet — feel free to message us your question directly and we'll help you out!
                        </span>
                      ) : (
                        <>
                          <span className="font-semibold">Frequently Asked Questions:</span>
                          {form.faqs
                            .filter((f) => f.question.trim() && f.answer.trim())
                            .map((f, i) => (
                              <p key={i} className="mt-2">
                                <span className="font-semibold">{i + 1}. {f.question.trim()}</span>
                                <br />
                                {f.answer.trim()}
                              </p>
                            ))}
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Abandoned cart reminders — Growth/Pro only */}
          <Section
            title="Abandoned cart reminders"
            caption={
              abandonedCartAvailable ? (
                <>
                  Auto-sends up to 2 DMs to a customer who added items to cart but never checked
                  out, then stops.{" "}
                  <Link to="/abandoned-cart-guide" className="font-semibold text-hotpink hover:underline">
                    See how it works →
                  </Link>
                </>
              ) : (
                "Available on Growth and Pro — auto-sends up to 2 reminder DMs to customers who leave items in their cart."
              )
            }
          >
            {!abandonedCartAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} could help boost your average order
                  value (AOV) — customers who come back to a reminder often add one more thing
                  before checking out.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">
                    Upgrade to Growth or Pro
                  </Link>{" "}
                  to turn it on, or{" "}
                  <Link to="/abandoned-cart-guide" className="font-semibold text-hotpink hover:underline">
                    see how it works →
                  </Link>
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => set("abandonedCartEnabled", true)}
                    data-testid="abandoned-cart-yes"
                    className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                      form.abandonedCartEnabled
                        ? "border-ink bg-ink text-cream"
                        : "border-ink/15 bg-white text-ink hover:border-ink/30"
                    }`}
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                        form.abandonedCartEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                      }`}
                    >
                      <Check className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold">Send reminders</span>
                      <span className={`block text-xs ${form.abandonedCartEnabled ? "text-cream/70" : "text-ink/50"}`}>
                        On by default
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => set("abandonedCartEnabled", false)}
                    data-testid="abandoned-cart-no"
                    className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                      !form.abandonedCartEnabled
                        ? "border-ink bg-ink text-cream"
                        : "border-ink/15 bg-white text-ink hover:border-ink/30"
                    }`}
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                        !form.abandonedCartEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"
                      }`}
                    >
                      <X className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold">Off</span>
                      <span className={`block text-xs ${!form.abandonedCartEnabled ? "text-cream/70" : "text-ink/50"}`}>
                        Never send cart reminders
                      </span>
                    </span>
                  </button>
                </div>

                {form.abandonedCartEnabled && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label="First reminder"
                      error={errors.abandonedCartNudge1Minutes}
                      hint="Minutes after they stop adding to cart — min 60"
                    >
                      <IconInput icon={Timer}>
                        <input
                          type="number"
                          min={60}
                          value={form.abandonedCartNudge1Minutes}
                          onChange={(e) =>
                            set("abandonedCartNudge1Minutes", e.target.value.replace(/[^\d]/g, "").slice(0, 4))
                          }
                          placeholder="60"
                          className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                          data-testid="abandoned-cart-nudge1"
                        />
                      </IconInput>
                    </Field>
                    <Field
                      label="Second reminder"
                      error={errors.abandonedCartNudge2Minutes}
                      hint="Minutes after cart activity — final message"
                    >
                      <IconInput icon={Timer}>
                        <input
                          type="number"
                          min={60}
                          value={form.abandonedCartNudge2Minutes}
                          onChange={(e) =>
                            set("abandonedCartNudge2Minutes", e.target.value.replace(/[^\d]/g, "").slice(0, 4))
                          }
                          placeholder="1200"
                          className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                          data-testid="abandoned-cart-nudge2"
                        />
                      </IconInput>
                    </Field>
                  </div>
                )}
              </>
            )}
          </Section>

          {/* Order shipping reminders — Starter/Growth/Pro only */}
          {shippingRemindersAvailable && (
            <Section
              title="Order shipping reminders"
              caption="We'll notify you on the dashboard if a paid order sits unshipped this long."
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => set("unshippedRemindersEnabled", true)}
                  data-testid="unshipped-reminders-yes"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    form.unshippedRemindersEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                      form.unshippedRemindersEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <Check className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Send reminders</span>
                    <span className={`block text-xs ${form.unshippedRemindersEnabled ? "text-cream/70" : "text-ink/50"}`}>
                      On by default
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => set("unshippedRemindersEnabled", false)}
                  data-testid="unshipped-reminders-no"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    !form.unshippedRemindersEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                      !form.unshippedRemindersEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <X className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Off</span>
                    <span className={`block text-xs ${!form.unshippedRemindersEnabled ? "text-cream/70" : "text-ink/50"}`}>
                      Never remind me to ship
                    </span>
                  </span>
                </button>
              </div>

              {form.unshippedRemindersEnabled && (
                <div className="mt-4">
                  <Field
                    label="Remind me after"
                    error={errors.unshippedReminderHours}
                    hint="Hours after payment — min 1, max 168 (7 days)"
                  >
                    <IconInput icon={Timer}>
                      <input
                        type="number"
                        min={1}
                        max={168}
                        value={form.unshippedReminderHours}
                        onChange={(e) =>
                          set("unshippedReminderHours", e.target.value.replace(/[^\d]/g, "").slice(0, 3))
                        }
                        placeholder="24"
                        className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                        data-testid="unshipped-reminder-hours"
                      />
                    </IconInput>
                  </Field>
                </div>
              )}
            </Section>
          )}

          {/* Low stock alerts — Growth/Pro only */}
          <Section
            title="Low stock alerts"
            caption={
              lowStockAlertsAvailable
                ? "We'll notify you on the dashboard when a product or size you've flagged runs low. Set the alert level per product/size in Catalog."
                : "Available on Growth and Pro — notifies you when a product or size runs low, so you can restock in time."
            }
          >
            {!lowStockAlertsAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} helps you avoid selling out unnoticed.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">
                    Upgrade to Growth or Pro
                  </Link>{" "}
                  to turn it on.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => set("lowStockAlertsEnabled", true)}
                  data-testid="low-stock-alerts-yes"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    form.lowStockAlertsEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                      form.lowStockAlertsEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <Check className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Send alerts</span>
                    <span className={`block text-xs ${form.lowStockAlertsEnabled ? "text-cream/70" : "text-ink/50"}`}>
                      On by default
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => set("lowStockAlertsEnabled", false)}
                  data-testid="low-stock-alerts-no"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    !form.lowStockAlertsEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                      !form.lowStockAlertsEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <X className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Off</span>
                    <span className={`block text-xs ${!form.lowStockAlertsEnabled ? "text-cream/70" : "text-ink/50"}`}>
                      Never send low stock alerts
                    </span>
                  </span>
                </button>
              </div>
            )}
          </Section>

          {/* Catalog randomization — Pro only */}
          <Section
            title="Randomize catalog order in DMs"
            caption={
              catalogRandomizeAvailable
                ? "Shuffles product order for each customer so older or less-viewed items get seen too — great for clearing dead stock. Bestsellers and new arrivals still show first."
                : "Available on Pro — shuffles catalog order per customer to surface dead stock, while bestsellers and new arrivals still show first."
            }
          >
            {!catalogRandomizeAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} helps rotate which products customers see first.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">Upgrade to Pro</Link> to turn it on.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => set("catalogRandomizeEnabled", true)}
                  data-testid="catalog-randomize-yes"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    form.catalogRandomizeEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${form.catalogRandomizeEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"}`}>
                    <Check className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Shuffle catalog order</span>
                    <span className={`block text-xs ${form.catalogRandomizeEnabled ? "text-cream/70" : "text-ink/50"}`}>Off by default</span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => set("catalogRandomizeEnabled", false)}
                  data-testid="catalog-randomize-no"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    !form.catalogRandomizeEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${!form.catalogRandomizeEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"}`}>
                    <X className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Off</span>
                    <span className={`block text-xs ${!form.catalogRandomizeEnabled ? "text-cream/70" : "text-ink/50"}`}>Fixed order (default)</span>
                  </span>
                </button>
              </div>
            )}
          </Section>

          {/* Cross-sell suggestions — Pro only */}
          <Section
            title="Cross-sell suggestions"
            caption={
              crossSellAvailable
                ? "We'll show 3 related products in the order confirmation email, and 3 different ones in the shipping email — based on what other customers have bought together."
                : "Available on Pro — shows 3 related products in the order confirmation email, and 3 more in the shipping email, based on past order pairings."
            }
          >
            {!crossSellAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} is an easy way to turn a one-time buyer into a repeat customer.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">
                    Upgrade to Pro
                  </Link>{" "}
                  to turn it on.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => set("crossSellEnabled", true)}
                  data-testid="cross-sell-yes"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    form.crossSellEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                      form.crossSellEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <Check className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Show suggestions</span>
                    <span className={`block text-xs ${form.crossSellEnabled ? "text-cream/70" : "text-ink/50"}`}>
                      Off by default
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => set("crossSellEnabled", false)}
                  data-testid="cross-sell-no"
                  className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                    !form.crossSellEnabled
                      ? "border-ink bg-ink text-cream"
                      : "border-ink/15 bg-white text-ink hover:border-ink/30"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                      !form.crossSellEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <X className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold">Off</span>
                    <span className={`block text-xs ${!form.crossSellEnabled ? "text-cream/70" : "text-ink/50"}`}>
                      Never show cross-sell suggestions
                    </span>
                  </span>
                </button>
              </div>
            )}
          </Section>

          {/* Post-delivery review request email — Growth/Pro only */}
          <Section
            title="Post-delivery review request"
            collapsible={reviewRequestAvailable}
            open={reviewRequestOpen}
            onToggle={() => setReviewRequestOpen((o) => !o)}
            badge={
              reviewRequestAvailable ? (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    reviewRequestConfigured ? "bg-mint text-ink" : "bg-hotpink/10 text-hotpink"
                  }`}
                >
                  {!form.reviewRequestEnabled ? "Off" : reviewRequestConfigured ? "Set up" : "Needs setup"}
                </span>
              ) : null
            }
            caption={
              reviewRequestAvailable ? (
                <>
                  Sends one email per order, X days after you mark it shipped — asking for a
                  review on Instagram, with whatever offer you want to write in. Off by default.
                </>
              ) : (
                "Available on Growth and Pro — auto-sends a review-request email to customers a few days after their order ships."
              )
            }
          >
            {!reviewRequestAvailable ? (
              <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.03] p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink/5 text-ink/50">
                  <Lock className="h-4 w-4" />
                </span>
                <p className="text-sm text-ink/60">
                  {firstName ? `${firstName}, this` : "This"} is an easy way to turn happy buyers into
                  reviews and repeat orders.{" "}
                  <Link to="/plans" className="font-semibold text-hotpink hover:underline">
                    Upgrade to Growth or Pro
                  </Link>{" "}
                  to turn it on.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={applyReviewRequestDefaults}
                    data-testid="review-request-yes"
                    className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                      form.reviewRequestEnabled
                        ? "border-ink bg-ink text-cream"
                        : "border-ink/15 bg-white text-ink hover:border-ink/30"
                    }`}
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                        form.reviewRequestEnabled ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"
                      }`}
                    >
                      <Check className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold">Send review request</span>
                      <span className={`block text-xs ${form.reviewRequestEnabled ? "text-cream/70" : "text-ink/50"}`}>
                        Off by default
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => set("reviewRequestEnabled", false)}
                    data-testid="review-request-no"
                    className={`flex items-center gap-2 rounded-2xl border p-4 text-left transition-colors ${
                      !form.reviewRequestEnabled
                        ? "border-ink bg-ink text-cream"
                        : "border-ink/15 bg-white text-ink hover:border-ink/30"
                    }`}
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                        !form.reviewRequestEnabled ? "bg-hotpink text-white" : "bg-ink/5 text-ink/40"
                      }`}
                    >
                      <X className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold">Off</span>
                      <span className={`block text-xs ${!form.reviewRequestEnabled ? "text-cream/70" : "text-ink/50"}`}>
                        Never send review request emails
                      </span>
                    </span>
                  </button>
                </div>

                {form.reviewRequestEnabled && (
                  <div className="mt-4 space-y-4">
                    <Field
                      label="Send after"
                      error={errors.reviewRequestDelayDays}
                      hint="Days after you mark an order shipped — min 1, max 90"
                    >
                      <IconInput icon={Timer}>
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={form.reviewRequestDelayDays}
                          onChange={(e) =>
                            set("reviewRequestDelayDays", e.target.value.replace(/[^\d]/g, "").slice(0, 2))
                          }
                          placeholder="7"
                          className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                          data-testid="review-request-delay"
                        />
                      </IconInput>
                    </Field>

                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink/70">
                        Template
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {REVIEW_REQUEST_TEMPLATES.map((t) => {
                          const active =
                            form.reviewRequestEmailSubject === t.subject &&
                            form.reviewRequestEmailBody === t.body;
                          return (
                            <button
                              type="button"
                              key={t.key}
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  reviewRequestEmailSubject: t.subject,
                                  reviewRequestEmailBody: t.body,
                                }))
                              }
                              data-testid={`review-request-template-${t.key}`}
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                active
                                  ? "border-ink bg-ink text-cream"
                                  : "border-ink/15 bg-white text-ink/70 hover:border-ink/40"
                              }`}
                            >
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-1.5 text-[11px] text-ink/40">
                        Picking a template replaces the subject and body below — edit freely afterward.
                      </p>
                    </div>

                    <Field
                      label="Email subject"
                      error={errors.reviewRequestEmailSubject}
                      hint="You can use {{customer_name}}, {{order_number}}, {{business_name}}"
                    >
                      <IconInput icon={Mail}>
                        <input
                          value={form.reviewRequestEmailSubject}
                          onChange={(e) => set("reviewRequestEmailSubject", e.target.value)}
                          placeholder="How was your order from {{business_name}}?"
                          className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none"
                          data-testid="review-request-subject"
                        />
                      </IconInput>
                    </Field>

                    <Field
                      label="Email body"
                      error={errors.reviewRequestEmailBody}
                      hint={`${form.reviewRequestEmailBody.length} chars · write whatever offer you want — a % off, a coupon code you've created, or just a thank-you`}
                    >
                      <textarea
                        rows={7}
                        value={form.reviewRequestEmailBody}
                        onChange={(e) => set("reviewRequestEmailBody", e.target.value)}
                        placeholder={
                          "Hi {{customer_name}},\n\nHope you're loving your order ({{order_number}})!\n\n" +
                          "{{items_text}}\n\nIf you have a minute, we'd really appreciate a review on our " +
                          "Instagram post or Story — just tag us or send us a screenshot!\n\n" +
                          "As a thank you, here's 5% OFF your next order (valid for 1 month) — DM us and " +
                          "we'll send you a code.\n\n-- {{business_name}}"
                        }
                        className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                        data-testid="review-request-body"
                      />
                    </Field>

                    <p className="rounded-xl bg-ink/[0.03] px-3 py-2 text-xs text-ink/50">
                      Want to offer a real discount? Create a coupon code in{" "}
                      <Link to="/shipping" className="font-semibold text-hotpink hover:underline">
                        Shipping & Discounts
                      </Link>{" "}
                      first (set its own expiry there), then paste the code into the body above —
                      customers can redeem it the normal way, by DMing it in at checkout.
                    </p>
                  </div>
                )}
              </>
            )}
          </Section>

          {/* Save bar */}
          <div className="sticky bottom-4 z-30 flex flex-col items-stretch gap-3 rounded-3xl border border-ink/10 bg-white/90 px-5 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-ink/60">
              Payouts in <span className="font-semibold text-ink">{activeCountry.currency}</span> ·
              Powered by direct transfers, zero commission.
            </p>
            <div className="flex items-center gap-2">
              <AnimatePresence>
                {saved && (
                  <motion.span
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    className="inline-flex items-center gap-1 rounded-full bg-mint px-3 py-1 text-xs font-semibold text-ink"
                    data-testid="profile-saved"
                  >
                    <Check className="h-3.5 w-3.5" /> Saved
                  </motion.span>
                )}
              </AnimatePresence>
              <button
                type="submit"
                disabled={saving}
                data-testid="profile-save-btn"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-60 sm:w-auto"
              >
                {saving ? "Saving…" : "Save profile"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

// -------------------- Reusable subcomponents --------------------------------
function Section({ title, caption, children, collapsible, open, onToggle, badge }) {
  if (!collapsible) {
    return (
      <div className="rounded-3xl border border-ink/10 bg-white p-6 md:p-8">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-ink">{title}</h2>
          {caption && <p className="mt-1 whitespace-pre-line text-sm text-ink/50">{caption}</p>}
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    );
  }
  return (
    <div className="rounded-3xl border border-ink/10 bg-white p-6 md:p-8">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-ink">{title}</h2>
            {badge}
          </div>
          {caption && <p className="mt-1 whitespace-pre-line text-sm text-ink/50">{caption}</p>}
        </div>
        <span className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink/10 bg-ink/[0.03] text-ink/60">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-6 space-y-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, required, hint, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink/70">
        {label}
        {required && <span className="ml-1 text-hotpink">*</span>}
      </span>
      {children}
      {/* Hint sits below the input, not below the label — so every
          label row is exactly one line and inputs in the same grid
          row always start at the same height, whether or not that
          particular field has a hint. */}
      {hint && !error && (
        <span className="mt-1 block text-[10px] font-medium text-ink/40">{hint}</span>
      )}
      {error && <span className="mt-1 block text-xs font-semibold text-hotpink">{error}</span>}
    </label>
  );
}

function IconInput({ icon: Icon, children }) {
  return (
    <div className="relative rounded-2xl border border-ink/15 bg-white transition-colors focus-within:border-hotpink">
      <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
      {children}
    </div>
  );
}
